/**
 * Orders Management - Dataverse API Integration
 */

import { ICommonListApiRes, NetworkMethodEnum } from "@/types";
import { MiddlewareAPI } from "@/lib/axios";
import { fetcher } from "@/utils/api/dataverse/fetcher";
import { generateQueryParamsForPagination } from "@/utils/pagination";
import {
  IDataverseOrder,
  IOrderQueryParams,
  ITotalCountResponse,
  ITotalValueResponse,
  IAllOrderCountsByStatusResponse,
} from "../types";
import { mutator } from "@/utils/api/dataverse/mutator";

export const URL = {
  orders: "/OrderApi",
  createPayment: "/CreatePaymentApi",
};

export interface ICreatePaymentPayload {
  total: number;
  currency: string;
  userPaymentId: string;
  email: string;
  phone: string;
  orderid: string;
  destination: 'email' | 'sms';
}

/**
 * Get orders list from Dataverse with pagination and filters
 */
export const getOrderListFromDataverse = async (
  queryParams?: IOrderQueryParams
): Promise<ICommonListApiRes<IDataverseOrder>> => {
  const query = generateQueryParamsForPagination(queryParams);
  return await fetcher(
    `${URL.orders}${query}&$orderby=createdon desc&$expand=quoteid($select=quoteid,quotenumber,dogma_region,dogma_projectreference,dogma_projecttype,_dogma_customer_value,dogma_exchangerate,createdon,dogma_vssdiscountpercentage;$expand=dogma_DealersCurrency($select=dogma_name,dogma_currencysymbol,dogma_isocurrencycode,dogma_exchangerate,dogma_vatpercentage))`,
    queryParams?.pageSize,
  );
};

/**
 * Get single order by ID from Dataverse with full expansions
 */
export const getOrderDetail = async (id: string): Promise<IDataverseOrder> => {
  const response = await MiddlewareAPI.get<IDataverseOrder>(
    URL.orders +
      "?$expand=customerid_contact($select=firstname,lastname,telephone1,emailaddress1,address1_city,address1_country,address1_line1,address1_postalcode),quoteid($select=quoteid,quotenumber,dogma_projectreference,dogma_projecttype,dogma_region,dogma_exchangerate,createdon,dogma_vssdiscountpercentage,_dogma_customer_value;$expand=dogma_DealersCurrency($select=dogma_name,dogma_currencysymbol,dogma_isocurrencycode,dogma_exchangerate,dogma_vatpercentage))",
    { params: { id } }
  );
  return response.data;
};

/**
 * Get total orders count using aggregation (no 5k limit)
 */
export const getTotalOrdersCountApi = async (
  region?: number | null
): Promise<ITotalCountResponse> => {
  let query = `${URL.orders}?$apply=`;

  if (region !== null && region !== undefined && region !== 0) {
    query += `filter(quoteid/dogma_region eq ${region})/`;
  }

  query += `aggregate($count as count)`;

  return await fetcher(query);
};

/**
 * Get total orders value using aggregation (no 5k limit)
 */
export const getTotalOrdersValueApi = async (): Promise<ITotalValueResponse> => {
  return await fetcher(
    `${URL.orders}?$apply=aggregate(dogma_vsstotalamount with sum as totalValue)`
  );
};

/**
 * Get order counts by status using aggregation (no 5k limit)
 */
export const getOrderCountByStatusApi = async (
  statuscode?: number,
  region?: number | null
): Promise<ITotalCountResponse> => {
  const conditions: string[] = [];
  if (statuscode !== undefined) {
    conditions.push(`statuscode eq ${statuscode}`);
  }
  if (region !== null && region !== undefined && region !== 0) {
    conditions.push(`quoteid/dogma_region eq ${region}`);
  }

  let query = `${URL.orders}?$apply=`;
  if (conditions.length > 0) {
    query += `filter(${conditions.join(' and ')})/`;
  }
  query += `aggregate($count as count)`;

  return await fetcher(query);
};

/**
 * Get all order counts grouped by status using OData aggregation
 */
export const getAllOrderCountsByStatusApi = async (
  region?: number | null
): Promise<IAllOrderCountsByStatusResponse> => {
  let query = `${URL.orders}?$apply=`;

  if (region !== null && region !== undefined && region !== 0) {
    query += `filter(quoteid/dogma_region eq ${region})/`;
  }

  query += `groupby((statuscode), aggregate($count as count))`;

  return await fetcher(query);
};

/**
 * Get outstanding payments value (orders with non-fully-paid payment status)
 * Payment Status: 1 (Payment Pending), 3 (Deposit Paid), 4 (Partially Paid), 5 (Overdue)
 */
export const getOutstandingPaymentsValueApi = async (
  region?: number | null
): Promise<ITotalValueResponse> => {
  const paymentStatusFilter = `(dogma_paymentstatus eq 1 or dogma_paymentstatus eq 3 or dogma_paymentstatus eq 4 or dogma_paymentstatus eq 5 or dogma_paymentstatus eq 7)`;
  const filter = region !== null && region !== undefined && region !== 0
    ? `${paymentStatusFilter} and quoteid/dogma_region eq ${region}`
    : paymentStatusFilter;

  return await fetcher(
    `${URL.orders}?$apply=filter(${filter})/aggregate(dogma_vsstotalamount with sum as totalValue)`,
  );
};

export const createPaymentApi = async (
  payload: ICreatePaymentPayload
): Promise<{ success: boolean; paymentId?: string }> => {
  return await mutator(NetworkMethodEnum.POST, URL.createPayment, payload as unknown as Record<string, unknown>);
};

/**
 * Generic order update API - can update any order field(s)
 */
export const updateOrderApi = async (
  payload: any
): Promise<IDataverseOrder> => {
  return await mutator(NetworkMethodEnum.PATCH, `${URL.orders}`, payload);
};

/**
 * Fulfill sales order - Change order status to completed
 */
export const fulfillSalesOrderApi = async (payload: {
  OrderClose: {
    subject: string;
    "salesorderid@odata.bind": string;
  };
  Status: number;
}): Promise<any> => {
  return await mutator(
    NetworkMethodEnum.POST,
    `/FulfillSalesOrder`,
    payload
  );
};

/**
 * Cancel sales order - Change order status to cancelled
 */
export const cancelSalesOrderApi = async (payload: {
  OrderClose: {
    subject: string;
    salesorderid: string;
    "salesorderid@odata.bind": string;
  };
  Status: number;
}): Promise<any> => {
  return await mutator(
    NetworkMethodEnum.POST,
    `/CancelSalesOrder`,
    payload
  );
};
